import { useEffect, useRef } from "react";
import { getSocket } from "../Api/socket";

/**
 * Drive live UI from Socket.IO. REST-poll only while the socket is missing or disconnected.
 */
export default function useSocketLiveRefresh({
  enabled = true,
  role = "patient",
  events = [],
  onEvent,
  pollMs = 8000,
  getSocketClient,
} = {}) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const eventsKey = Array.isArray(events) ? events.join("|") : "";

  useEffect(() => {
    if (!enabled) return undefined;

    const eventList = eventsKey ? eventsKey.split("|").filter(Boolean) : [];
    let pollId = null;

    const refresh = (...args) => {
      try {
        onEventRef.current?.(...args);
      } catch (_) {}
    };

    const startPolling = () => {
      if (pollId || !pollMs) return;
      pollId = window.setInterval(() => refresh(), pollMs);
    };

    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    const socket =
      typeof getSocketClient === "function" ? getSocketClient() : getSocket({ role });

    if (!socket) {
      startPolling();
      return () => stopPolling();
    }

    const onConnect = () => {
      stopPolling();
      refresh();
    };

    for (const ev of eventList) socket.on(ev, refresh);
    socket.on("connect", onConnect);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);

    if (socket.connected) stopPolling();
    else startPolling();

    return () => {
      for (const ev of eventList) {
        try {
          socket.off(ev, refresh);
        } catch (_) {}
      }
      try {
        socket.off("connect", onConnect);
        socket.off("disconnect", startPolling);
        socket.off("connect_error", startPolling);
      } catch (_) {}
      stopPolling();
    };
  }, [enabled, role, eventsKey, pollMs, getSocketClient]);
}
